import { ExternalLink, X } from "lucide-react";
import { copy } from "../copy";
import { useEffect, useState, type ReactNode } from "react";
import { publishUrgency } from "../domain/calendar";
import { PLATFORMS } from "../domain/platforms";
import { isTopicStatus } from "../domain/status";
import { todayInShanghai } from "../domain/time";
import { formatWhen } from "../lib/format";
import { plainText } from "../lib/text";
import { useApp } from "../state/store";
import { TOPIC_STATUSES, type Topic } from "../types";
import { GenerateMenu } from "./GenerateMenu";
import { StagePill } from "./StagePill";

export function DetailModal({ topic }: { topic: Topic }) {
  const app = useApp();
  const schema = app.notion.schema;
  const [publishUrl, setPublishUrl] = useState(topic.publishUrl ?? "");
  const [planned, setPlanned] = useState(topic.plannedPublishDate ?? "");
  const [platforms, setPlatforms] = useState(topic.platforms);
  const [notes, setNotes] = useState(topic.notes);
  const timeline = app.state.activities
    .filter((item) => item.topicId === topic.id && item.deletedAt == null)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const urgency = publishUrgency(topic.plannedPublishDate, topic.status, todayInShanghai());

  useEffect(() => {
    setPublishUrl(topic.publishUrl ?? "");
    setPlanned(topic.plannedPublishDate ?? "");
    setPlatforms(topic.platforms);
    setNotes(topic.notes);
  }, [topic.id, topic.updatedAt, topic.publishUrl, topic.plannedPublishDate, topic.notes, topic.platforms]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") app.openTopic(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [app]);

  return (
    <div className="drawer-backdrop" onMouseDown={() => app.openTopic(null)}>
      <div
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="topic-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <span className="eyebrow">选题</span>
            <h2 id="topic-title">{plainText(topic.title)}</h2>
            <StagePill status={topic.status} />
            <GenerateMenu topic={topic} />
          </div>
          <button type="button" onClick={() => app.openTopic(null)} aria-label="关闭">
            <X size={18} />
          </button>
        </header>
        <div className="drawer-body">
          <label className="status-select">
            <span>状态</span>
            <select
              value={topic.status}
              aria-label="状态"
              onChange={(event) => {
                const next = event.target.value;
                if (isTopicStatus(next)) app.setStatus(topic.id, next);
              }}
            >
              {TOPIC_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </label>
          {topic.syncError ? <p className="inline-error">{topic.syncError}</p> : null}
          <FactList topic={topic} urgency={urgency} editablePlan={Boolean(schema?.plannedDate)} />
          <p className="link-row">
            <a href={topic.notionUrl} target="_blank" rel="noreferrer">
              在 Notion 中打开 <ExternalLink size={14} />
            </a>
            {topic.publishUrl ? (
              <a href={topic.publishUrl} target="_blank" rel="noreferrer">
                打开发布链接 <ExternalLink size={14} />
              </a>
            ) : null}
          </p>
          <form
            id="topic-fields"
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              app.saveFields(topic.id, {
                notes,
                ...(schema?.publishUrl ? { publishUrl: publishUrl.trim() || null } : {}),
                ...(schema?.plannedDate ? { plannedPublishDate: planned || null } : {}),
                ...(schema?.platforms ? { platforms } : {}),
              });
            }}
          >
            <h3>发布信息</h3>
            <label>
              <span>发布链接</span>
              <input
                value={schema?.publishUrl ? publishUrl : topic.publishUrl ?? ""}
                onChange={(event) => setPublishUrl(event.target.value)}
                placeholder={schema?.publishUrl ? "选填，笔记链接" : "选题库里还没有「发布链接」"}
                disabled={!schema?.publishUrl}
              />
            </label>
            <label>
              <span>计划发布日</span>
              <input
                type="date"
                value={schema?.plannedDate ? planned : topic.plannedPublishDate ?? ""}
                onChange={(event) => setPlanned(event.target.value)}
                disabled={!schema?.plannedDate}
              />
            </label>
            <fieldset disabled={!schema?.platforms}>
              <legend>发布平台</legend>
              <div className="checks">
                {PLATFORMS.map((platform) => (
                  <label key={platform.id}>
                    <input
                      type="checkbox"
                      checked={platforms.includes(platform.label)}
                      onChange={(event) => {
                        setPlatforms((current) =>
                          event.target.checked ? [...current, platform.label] : current.filter((item) => item !== platform.label),
                        );
                      }}
                    />
                    {platform.label}
                  </label>
                ))}
              </div>
              {!schema?.platforms ? <small>选题库里还没有「发布平台」，所以不能写回。</small> : null}
            </fieldset>
            <label>
              <span>备注</span>
              <textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={4} />
              <small>{schema?.notes ? "备注会写回 Notion。" : "选题库没有「备注」时，这段话只保存在 MediaDesk。"}</small>
            </label>
          </form>
          <section>
            <h3>操作记录</h3>
            {timeline.length === 0 ? (
              <p className="muted">还没有操作记录。</p>
            ) : (
              <div className="timeline">
                {timeline.map((item) => (
                  <div key={item.id} className="timeline-row">
                    <time>{formatWhen(item.createdAt)}</time>
                    <div>
                      <b>{item.type === "sync_conflict" ? "以 Notion 为准" : "状态变化"}</b>
                      <p>{item.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
        <footer>
          <button type="button" className="button" onClick={() => app.openTopic(null)}>
            关闭
          </button>
          <button type="submit" form="topic-fields" className="button primary">
            保存
          </button>
        </footer>
      </div>
    </div>
  );
}

function FactList({ topic, urgency, editablePlan }: { topic: Topic; urgency: string; editablePlan: boolean }) {
  const plan = `${topic.plannedPublishDate || "未定"}${urgency === "overdue" ? " · 已过期" : ""}`;
  const facts: Array<{ label: string; text: string; node: ReactNode; className?: string }> = [
    { label: "批次", text: plainText(topic.batch) || "未分批", node: plainText(topic.batch) || "未分批" },
    { label: "切入角度", text: plainText(topic.angle), node: plainText(topic.angle) || copy.dash },
    { label: "目标人群", text: plainText(topic.audience), node: plainText(topic.audience) || copy.dash },
    { label: "参考来源", text: plainText(topic.sources), node: <SourceValue value={plainText(topic.sources)} /> },
    { label: "创建日期", text: topic.createdDate || "", node: topic.createdDate || copy.dash },
    ...(editablePlan
      ? []
      : [{
          label: "计划发布",
          text: topic.plannedPublishDate || "未定",
          node: plan,
          className: urgency === "overdue" ? "urgency-overdue" : urgency === "soon" ? "urgency-soon" : "",
        }]),
    { label: copy.fields.source, text: plainText(topic.sourceAssistant), node: plainText(topic.sourceAssistant) || copy.dash },
    { label: copy.fields.reason, text: plainText(topic.reason), node: plainText(topic.reason) || copy.dash },
    {
      label: copy.fields.heat,
      text: topic.heat == null ? "" : String(topic.heat),
      node: <span title={plainText(topic.heatBasis) || copy.noHeatBasis}>{topic.heat ?? copy.dash}</span>,
    },
    { label: copy.fields.heatBasis, text: plainText(topic.heatBasis), node: plainText(topic.heatBasis) || copy.dash },
    { label: copy.fields.reference, text: topic.referenceUrl ?? "", node: topic.referenceUrl ? <SourceValue value={topic.referenceUrl} /> : copy.dash },
    { label: copy.fields.queue, text: topic.queueOrder == null ? "" : String(topic.queueOrder), node: topic.queueOrder ?? copy.dash },
    { label: copy.fields.actual, text: topic.actualPublishAt?.slice(0, 10) ?? "", node: topic.actualPublishAt?.slice(0, 10) || copy.dash },
  ];
  const filled = facts.filter((fact) => fact.text.trim() !== "");
  const missing = facts.filter((fact) => fact.text.trim() === "").map((fact) => fact.label);
  return (
    <>
      {filled.length ? (
        <dl className="facts">
          {filled.map((fact) => (
            <div key={fact.label}>
              <dt>{fact.label}</dt>
              <dd className={fact.className}>{fact.node}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {missing.length ? <p className="muted fact-gap">未填写：{missing.join("、")}</p> : null}
    </>
  );
}

function SourceValue({ value }: { value: string }): ReactNode {
  const text = value.trim();
  if (!text) return "—";
  const chunks = text.split(/(\s+)/);
  if (!chunks.some((chunk) => /^https?:\/\//i.test(chunk))) return text;
  return chunks.map((chunk, index) =>
    /^https?:\/\//i.test(chunk) ? (
      <a key={`${chunk}-${index}`} href={chunk} target="_blank" rel="noreferrer">
        {chunk}
      </a>
    ) : (
      <span key={`${index}-${chunk}`}>{chunk}</span>
    ),
  );
}
